import { useState } from "react";
import { Send, MessageCircle, Phone, Mail, Clock, CheckCheck, Smile } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

const quickReplies = [
  "How do I apply?",
  "Can I get a scholarship?",
  "What courses are available?",
  "Is it really free?",
  "Help with overseas visa",
];

type Message = { from: "user" | "bot"; text: string; time: string };

const getTime = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const botReplies: Record<string, string> = {
  "How do I apply?":
    "Super easy! Create a free profile in 5 minutes, and our counselors will match you with the best universities. 🚀",
  "Can I get a scholarship?":
    "Yes! We have access to 100+ scholarships, including full rides. Share your grades and we'll find the best options for you 🎓",
  "What courses are available?":
    "Everything from Medicine & Law to AI & Design — across 300+ universities. Tell us your interests and we'll personalise your shortlist!",
  "Is it really free?":
    "100% free. No hidden fees, no application costs. We are paid by universities, not students. Your future shouldn't cost you extra 💜",
  "Help with overseas visa":
    "We handle the entire visa process for you — documentation, interviews, and tracking. You focus on packing. ✈️",
};

const channels = [
  {
    icon: <Phone className="w-5 h-5" />,
    label: "WhatsApp Us",
    value: "+60 11-1287 0256",
    color: "text-green-600",
    bg: "bg-green-50",
    href: "#",
  },
  {
    icon: <Mail className="w-5 h-5" />,
    label: "Email Us",
    value: "ng.scseducation@gmail.com",
    color: "text-violet-600",
    bg: "bg-violet-50",
    href: "#",
  },
  {
    icon: <Clock className="w-5 h-5" />,
    label: "Office Hours",
    value: "Mon–Fri, 9.30am–6.30pm",
    color: "text-orange-600",
    bg: "bg-orange-50",
    href: null,
  },
];

export function ContactSection() {
  const [messages, setMessages] = useState<Message[]>([
    {
      from: "bot",
      text: "Hey there! 👋 I'm your SCS Education assistant. What can I help you with today?",
      time: getTime(),
    },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = { from: "user", text, time: getTime() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setTyping(true);

    setTimeout(() => {
      const reply =
        botReplies[text] ||
        "Great question! Let me connect you with a real counselor for a detailed answer. We'll reach out in minutes 😊";
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: reply, time: getTime() },
      ]);
      setTyping(false);
    }, 1200);
  };

  return (
    <section className="py-24 lg:py-32 bg-white relative overflow-hidden" id="contact">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-100 to-transparent" />

      {/* BG blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-violet-50/80 blur-3xl" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-cyan-50/60 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left — Copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 mb-6">
              <MessageCircle className="w-3.5 h-3.5 text-slate-600" />
              <span
                className="text-[12px] text-slate-600 uppercase tracking-widest"
                style={{ fontFamily: "Inter, sans-serif", fontWeight: 700 }}
              >
                Talk to Us
              </span>
            </div>

            <h2
              className="text-[40px] lg:text-[52px] text-slate-900 tracking-tight mb-4"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, lineHeight: 1.1 }}
            >
              Got questions?{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, #7C3AED, #06B6D4)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Drop us a text.
              </span>
            </h2>

            <p
              className="text-[16px] text-slate-500 mb-10 max-w-md leading-relaxed"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
            >
              No long forms. No waiting rooms. Just a real conversation with a real counselor who
              gets what you're going through.
            </p>

            {/* Channels */}
            <div className="flex flex-col gap-4 mb-10">
              {channels.map((ch, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-2xl ${ch.bg} flex items-center justify-center ${ch.color}`}>
                    {ch.icon}
                  </div>
                  <div>
                    <p
                      className="text-[12px] text-slate-400"
                      style={{ fontFamily: "Inter, sans-serif", fontWeight: 500 }}
                    >
                      {ch.label}
                    </p>
                    {ch.href ? (
                      <a
                        href={ch.href}
                        className={`text-[14px] ${ch.color} hover:underline`}
                        style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
                      >
                        {ch.value}
                      </a>
                    ) : (
                      <p
                        className="text-[14px] text-slate-700"
                        style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
                      >
                        {ch.value}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Response time badge */}
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-green-50 border border-green-100 w-fit">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span
                className="text-[12px] text-green-700"
                style={{ fontFamily: "Inter, sans-serif", fontWeight: 600 }}
              >
                Typical response time: under 2 minutes
              </span>
            </div>
          </motion.div>

          {/* Right — Chat UI */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="bg-white rounded-3xl shadow-2xl shadow-slate-900/10 border border-slate-100 overflow-hidden">
              {/* Chat header */}
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                  <Smile className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p
                    className="text-[14px] text-white"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 700 }}
                  >
                    SCS Education Support
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    <span
                      className="text-[11px] text-violet-200"
                      style={{ fontFamily: "Inter, sans-serif", fontWeight: 500 }}
                    >
                      Online now
                    </span>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="h-72 overflow-y-auto px-4 py-5 flex flex-col gap-3 bg-[#F8F8FB]">
                <AnimatePresence>
                  {messages.map((msg, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] ${
                          msg.from === "user"
                            ? "bg-gradient-to-br from-violet-600 to-purple-600 text-white rounded-3xl rounded-br-lg"
                            : "bg-white text-slate-700 rounded-3xl rounded-bl-lg shadow-sm border border-slate-100"
                        } px-4 py-3`}
                      >
                        <p
                          className="text-[13px] leading-relaxed"
                          style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
                        >
                          {msg.text}
                        </p>
                        <div
                          className={`flex items-center gap-1 mt-1 ${
                            msg.from === "user" ? "justify-end" : "justify-start"
                          }`}
                        >
                          <span
                            className={`text-[10px] ${
                              msg.from === "user" ? "text-violet-200" : "text-slate-400"
                            }`}
                            style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
                          >
                            {msg.time}
                          </span>
                          {msg.from === "user" && (
                            <CheckCheck className="w-3 h-3 text-violet-200" />
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {typing && (
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex justify-start"
                    >
                      <div className="bg-white rounded-3xl rounded-bl-lg px-4 py-3 shadow-sm border border-slate-100">
                        <div className="flex gap-1">
                          {[0, 1, 2].map((i) => (
                            <span
                              key={i}
                              className="w-2 h-2 rounded-full bg-slate-400 animate-bounce"
                              style={{ animationDelay: `${i * 0.15}s` }}
                            />
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Quick replies */}
              <div className="px-4 py-3 flex gap-2 overflow-x-auto bg-white border-t border-slate-50 scrollbar-hide">
                {quickReplies.map((reply, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(reply)}
                    className="flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] text-violet-700 bg-violet-50 border border-violet-100 hover:bg-violet-100 transition-colors whitespace-nowrap"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 500 }}
                  >
                    {reply}
                  </button>
                ))}
              </div>

              {/* Input */}
              <div className="px-4 py-4 bg-white border-t border-slate-100 flex items-center gap-3">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2.5 rounded-2xl bg-slate-50 border border-slate-100 text-[13px] text-slate-700 placeholder-slate-400 outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100 transition-all"
                  style={{ fontFamily: "Inter, sans-serif", fontWeight: 400 }}
                />
                <button
                  onClick={() => sendMessage(input)}
                  className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 hover:-translate-y-px transition-all cursor-pointer"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
